import { css } from "styled-components";
import { colors } from "src/theme/colors";
import { line, radii } from "src/theme/ui";

/**
 * The chrome every field-shaped control in this folder borrows.
 *
 * Football's canvas draws a field once and reuses it everywhere: a white plane
 * at a 10px corner on the border edge, 12px of side padding, 13px text, under
 * an 11.5px/600 label with 6px of clearance. Focus moves the edge to the
 * accent line under a soft accent ring — it never glows, never lifts and never
 * gains a shadow, because in that system a shadow means "this can be
 * dismissed", which a field cannot.
 *
 * Kept in its own module, as the original keeps it in `Input`, so the select
 * and the search box land on the field's exact baseline rather than guessing
 * at it.
 */

/** Resting chrome. Height and edge are deliberately absent — each control
    states its own height and picks its edge by role. */
export const fieldChrome = css`
  border: 1px solid;
  border-radius: ${radii.md}px;
  background: ${colors.white};
  color: ${colors.gray[900]};
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background 0.15s ease;
`;

/** The edge weight, by role. See the note on `line.field`. */
export const fieldEdge = {
  /** A page control that filters what is already on screen. */
  control: line.hairline,
  /** A form field that collects a value. */
  field: line.field,
} as const;

/** The canvas's two field heights: a page control, and a form field. */
export const fieldHeight = { control: 34, field: 38 } as const;

export type FieldSize = keyof typeof fieldHeight;

/** Focus: the edge takes the accent line, under a soft accent ring. */
export const fieldFocus = css`
  &:focus,
  &:focus-visible,
  &:focus-within {
    border-color: ${colors.primary[200]};
    box-shadow: 0 0 0 4px ${colors.primary[15]};
  }
`;

/** The hovered edge, one step darker than resting. */
export const fieldHover = css`
  &:hover:not(:disabled):not(:focus-within) {
    border-color: ${colors.gray[400]};
  }
`;

/** The invalid edge, and its ring. */
export const fieldInvalid = css`
  border-color: ${colors.error[300]};

  &:focus,
  &:focus-visible,
  &:focus-within {
    border-color: ${colors.error[500]};
    box-shadow: 0 0 0 4px ${colors.error[100]};
  }
`;

/**
 * The menu panel a select or a dropdown opens: a 12px surface under the one
 * shadow it is allowed.
 */
export const menuPanel = css`
  overflow: hidden;
  padding: 6px;
  border: 1px solid ${line.hairline};
  border-radius: ${radii.menu}px;
  background: ${colors.white};
`;

/** The menu row: 34px, 9px of lead, an 8px corner, 12.5px/480. */
export const menuRow = css`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 9px;
  border-radius: ${radii.control}px;
  font-size: 12.5px;
  font-weight: 480;
  color: ${colors.gray[800]};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
`;
